/**
 * The stacking-context protocol, independent of any UI framework.
 *
 * "Stacking context" here means the nesting of overlay surfaces — modals, drop menus, popovers —
 * and the shared answer to three questions: is anything open, which surface is innermost, and how
 * deep does a given element sit. `StackableComponent` (Panel) and `useStackable()` (React) are both
 * front ends onto the one registry below; keeping the registry here rather than on the Panel class
 * is what lets a React surface participate without importing Panel.
 *
 * Membership order is load-bearing: `isCurrentStackingContextTarget` resolves "innermost" from the
 * Set's insertion order, which a separate count cannot reconstruct. Every participant must register
 * and unregister in balanced pairs — an id left behind suppresses every depth gate for the rest of
 * the page's life.
 */

export enum Visibility {
  Closed = `closed`,
  Open = `open`,
}

export type VisibilityChangeEvent = CustomEvent<{
  state: Visibility;
}>;

export const VisibilityEvents: {[x in Visibility]: string} = {
  [Visibility.Closed]: `stackable-closed`,
  [Visibility.Open]: `stackable-open`,
};

export const STACKABLE_CONTEXT_ATTR = `data-stackable-context`;

// elements that increase the stacking context
export const STACKABLE_ELEMENTS = [
  `mp-create-entity-modal`,
  `mp-custom-event-modal`,
  `mp-sharing-modal`,
  `mp-custom-property-modal`,
  `mp-drop-menu`,
  `mp-modal`,
  `mp-workspace-leave-modal`,
  // should we just add `#modals` here to capture modals created by react-modal-promise#create?
  `.inline-cohorts-modal-wrapper`,
  `[${STACKABLE_CONTEXT_ATTR}]`,
];

export const STACKABLE_ELEMENTS_SELECTOR = STACKABLE_ELEMENTS.join(`,`);

/**
 * Ids of every open stacking context, in the order they opened. Mutate it through the functions
 * below so ordering and balance stay intact; read it directly only for its `size`.
 */
export const contextStack = new Set<string>();

/**
 * Sets the current stacking context target to a given element id
 */
export function setCurrentStackingContextTarget(id: string) {
  contextStack.add(id);
}

/**
 * Removes the stacking context target from a given element id
 */
export function deleteCurrentStackingContextTarget(id: string) {
  contextStack.delete(id);
}

/**
 * Checks if a given element id is the current stacking context target
 */
export function isCurrentStackingContextTarget(id: string) {
  return Array.from(contextStack).pop() === id;
}

/**
 * Gets the depth of the current stacking context
 */
export function getCurrentStackingContextDepth() {
  return contextStack.size;
}

/**
 * Checks what the stacking context depth would be for the given element
 */
export function getStackingContextDepthForElement(el: Element | null | undefined): number {
  if (!el) {
    return 0;
  }
  const parent = el.parentElement || (el.getRootNode && (el.getRootNode() as ShadowRoot))?.host;
  return (el.matches && el.matches(STACKABLE_ELEMENTS_SELECTOR) ? 1 : 0) + getStackingContextDepthForElement(parent);
}
